import {AfterContentInit, Component, ContentChildren, Input, QueryList, Output, EventEmitter} from '@angular/core';
import {WcfTabComponent} from './wcf-tab.component';
import {ActivatedRoute, Router} from '@angular/router';

export class WcfTabChangeEvent {
    constructor(public index: number, public tab: WcfTabComponent) {}
}

export function throwErrorForMissingRouterLink(tabsWithoutRouterLink: WcfTabComponent[]) {
    const wcfTabTitles = tabsWithoutRouterLink.map(tab => tab.wcfTabTitle);
    throw Error(`Routerlink missing on ${wcfTabTitles.join(',')}`);
}

const supportedDirections = ['horizontal', 'vertical'];

export function validateDirectionInput(inputStr: string) {
    if (supportedDirections.indexOf(inputStr) < 0) {
        throw Error('Unsupported tab direction value: ' + inputStr);
    }
}

export function tabComponentMissing(): Error {
    return new Error(`TabSet must contain at least one TabComponent. Make sure to add a hc-tab to the hc-tab-set element.`);
}

export function invalidDefaultTab(tabVal: string) {
    throw Error('Invalid default tab value: ' + tabVal + ". Must be 'none' or a value less than the total number of tabs in the set.");
}

@Component({
    selector: `hc-wcf-tab-set`,
    templateUrl: './wcf-tab-set.component.html',
    styleUrls: ['../sass/_wcf-tabs.scss']
})
export class WcfTabSetComponent implements AfterContentInit {
    _routerEnabled: boolean = false;
    private _direction: string = 'vertical';
    private _defaultTab: string = '0';

    @ContentChildren(WcfTabComponent)
    _tabs: QueryList<WcfTabComponent>;

    /** Emits when the selected tab is changed */
    @Output()
    selectedTabChange: EventEmitter<WcfTabChangeEvent> = new EventEmitter();

    /** Specify direction of tabs as either `horizontal` or `vertical`. Defaults to `vertical` */
    @Input()
    get direction(): string {
        return this._direction;
    }

    set direction(directionType: string) {
        validateDirectionInput(directionType);
        this._direction = directionType;
    }

    /** Zero-based numerical value specifying which tab to select by default, setting to `none` means no tab
     * will be immediately selected. Defaults to 0 (the first tab). */
    @Input()
    get defaultTab(): string {
        return this._defaultTab;
    }

    set defaultTab(tabValue: string) {
        if (Number(tabValue) !== NaN || tabValue === 'none') {
            this._defaultTab = tabValue;
        } else {
            invalidDefaultTab(tabValue);
        }
    }

    constructor(private router: Router, private route: ActivatedRoute) {}

    ngAfterContentInit(): void {
        if (this._tabs.length === 0) {
            throw tabComponentMissing();
        }

        if (this.defaultTab !== 'none') {
            this.defaultToFirstTab();
        }
        this.checkForRouterUse();

        this._tabs.changes.subscribe(() => {
            if (this.defaultTab !== 'none') {
                this.defaultToFirstTab();
            }
            this.checkForRouterUse();
        });
    }

    /** Sets the currently selected tab by either its numerical index or `TabComponent` object  */
    selectTab(tab: number | WcfTabComponent) {
        if (typeof tab === 'number') {
            let i: number = 0;

            this._tabs.forEach(t => {
                if (i === tab) {
                    this._setActive(t);
                }
                i++;
            });
        } else {
            this._setActive(tab);
        }
    }

    _setActive(tab: WcfTabComponent, event?: Event) {
        let selectedTab: number = 0;
        let index: number = 0;

        this._tabs.forEach(t => {
            if (t === tab) {
                selectedTab = index;
            }
            t._active = false;
            index++;
        });

        tab.tabClick.emit(event);
        tab._active = true;
        this.selectedTabChange.emit(new WcfTabChangeEvent(selectedTab, tab));
    }

    private defaultToFirstTab() {
        if (!this._tabs.find(tab => tab._active)) {
            // setTimeout to avoid change after checked error
            // when ngFor is used as projected nodes are registered
            // and stored as part of the existing view, not
            // the view in which they are projected
            // embedded views are checked *before* AfterContentInit
            // is triggered
            const tabArray = this._tabs.toArray();
            if (tabArray[Number(this.defaultTab)]) {
                setTimeout(() => this._setActive(tabArray[Number(this.defaultTab)]));
            } else {
                invalidDefaultTab(this.defaultTab);
            }
        }
    }

    private checkForRouterUse() {
        if (this._tabs.length === 0) {
            return;
        }
        const countUsingRouter = this._tabs.filter(tab => tab.routerLink !== undefined).length;
        if (countUsingRouter > 0 && countUsingRouter < this._tabs.length) {
            const tabsMissingRouterLink = this._tabs.filter(tab => tab.routerLink === undefined);
            throwErrorForMissingRouterLink(tabsMissingRouterLink);
        }

        if (countUsingRouter === this._tabs.length) {
            this._routerEnabled = true;
            if (this._defaultTab !== 'none') {
                this.defaultToFirstRoute();
            }
        }
    }

    private defaultToFirstRoute() {
        const foundRoute = this._tabs
            .map(tab => tab.routerLink)
            .map(routerLink => this.mapRouterLinkToString(routerLink))
            .find(routerLink => {
                let currentRoute = this.router.url;
                return currentRoute === routerLink || currentRoute.indexOf(`${routerLink}/`) > -1;
            });

        if (foundRoute) {
            return;
        }

        const tabArray = this._tabs.toArray();
        if (tabArray[Number(this.defaultTab)]) {
            const firstRoute = this.mapRouterLinkToString(tabArray[Number(this.defaultTab)].routerLink);
            this.router.navigate([firstRoute], {relativeTo: this.route});
        }
    }

    private mapRouterLinkToString(routerLink: string | any[]): string {
        if (routerLink instanceof Array) {
            routerLink = routerLink.join('/').replace('//', '/');
        }
        return routerLink;
    }
}
